import React, { useState } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import DeviantArtLogo from '@wix/da-ds/pkg/Icons/DeviantArtLogo';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import {
  BannerLayout,
  BannerMessage,
  BannerButton,
} from '@wix/da-ds/pkg/Banner/lego';
import CloseIcon from '@wix/da-ds/pkg/Icons/16x16/Close';
import { usePWAInstallBanner } from './usePWAInstallBanner';
import { isiOS } from '../utils/browser';
import { BiData, type DefaultBiEvent } from '@wix/da-bi/pkg/events';

import s from './PWAInstallBanner.scss';

const PWAInstallBanner: React.FC = () => {
  const { t } = useTranslation();
  const { dismiss, isHidden, onInstall } = usePWAInstallBanner();
  const [isExpanded, setIsExpanded] = useState(false);

  if (isHidden) {
    return null;
  }
  return (
    <BannerLayout
      size={isExpanded ? 'multiline' : 'big'}
      className={c(s['root'], isExpanded && s['expanded'])}
      bodyClassName={s['body']}
      body={[
        isExpanded && (
          <div className={s['logo']}>
            <DeviantArtLogo size={IconSize.LARGE} disableDefaultColors />
          </div>
        ),
        !isExpanded && (
          <IconButton
            icon={CloseIcon}
            aria-label={t('common.close')}
            key="midfix-icon"
            size="small"
            iconClassName={s['icon']}
            onClick={dismiss}
            biData={BiData<DefaultBiEvent>({
              evid: 2,
              click_info: 'pwa_close',
            })}
          />
        ),
        <BannerMessage
          key="message-after-icon"
          weight="bld"
          className={s['your-typography-colors']}
        >
          <div className={s['title']}>{t('pwa.install.banner.title')}</div>
          {!isExpanded && (
            <div className={s['subtitle']}>
              {t('pwa.install.banner.subtitle')}
            </div>
          )}
          {isExpanded && isiOS() && (
            <div className={s['subtitle']}>
              {t('pwa.install.banner.iPhoneInstructions', {
                postProcess: ['reactPostprocessor', 'featureBrancher'],
                icon: (
                  <img
                    src="https://st.deviantart.net/eclipse/icons/pwa/ios-share.svg"
                    className={s['share-icon']}
                    alt="Share"
                  />
                ),
              })}
            </div>
          )}
        </BannerMessage>,
        isExpanded && (
          <TextButton
            size="large"
            type="button"
            variant="main"
            priority="primary"
            onClick={() => setIsExpanded(false)}
          >
            {t('pwa.install.banner.close')}
          </TextButton>
        ),
      ]}
      postfix={
        !isExpanded && (
          <BannerButton
            variant="solid"
            biData={BiData<DefaultBiEvent>({
              evid: 2,
              click_info: 'pwa_try_it',
            })}
            onClick={() => (isiOS() ? setIsExpanded(true) : onInstall())}
          >
            {t('pwa.install.banner.cta')}
          </BannerButton>
        )
      }
    />
  );
};
PWAInstallBanner.displayName = 'PWAInstallBanner';

export default PWAInstallBanner;
